import { useContext, useMemo } from 'react';

import { CommentsTrackingContext } from '@vimeo/comments-tracking';
import { CommentType } from '@vimeo/comments-ui';
import { useCapability } from '@vimeo/core/hooks/use-capability';

/**
 * Hook to enrich comments with read/unread status based on comments tracking context.
 *
 * Automatically checks:
 * 1. If hasCommentsTrackingEnabled capability is true
 * 2. If CommentsTrackingProvider is present
 *
 * If either condition is false, returns comments unchanged for backward compatibility.
 *
 * @param comments - Array of comments to enrich
 * @param showOnlyUnread - If true, filters comments to show only unread ones
 * @returns Enriched comments with isUnread property (if enabled) or original comments
 */
export const useEnrichCommentsWithTracking = (
  comments?: CommentType[],
  showOnlyUnread?: boolean,
) => {
  const { capabilities } = useCapability(['hasCommentsTrackingEnabled']);
  const isTrackingEnabled = capabilities?.hasCommentsTrackingEnabled === true;

  const trackingContext = useContext(CommentsTrackingContext);

  return useMemo(() => {
    if (!comments) return comments;

    if (!isTrackingEnabled || !trackingContext || !trackingContext.collaboratorUnreadCommentsIds) {
      return comments;
    }

    const { collaboratorUnreadCommentsIds, viewerUnreadCommentsIds } = trackingContext;

    const allUnreadIds = new Set([...collaboratorUnreadCommentsIds, ...viewerUnreadCommentsIds]);

    const enrichedComments = comments.map((comment) => {
      const isUnread = allUnreadIds.has(comment.id.split('/').pop() || '');

      // Enrich replies with read/unread status
      const enrichedReplies = comment.replies?.map((reply) => ({
        ...reply,
        isUnread: allUnreadIds.has(reply.id.split('/').pop() || ''),
      }));

      return {
        ...comment,
        isUnread,
        replies: enrichedReplies,
      };
    });

    // Filter to show only unread comments if the flag is set
    if (showOnlyUnread) {
      return enrichedComments.filter((comment) => comment.isUnread);
    }

    return enrichedComments;
  }, [comments, isTrackingEnabled, trackingContext, showOnlyUnread]);
};
